/**
 * Email validation using RFC 5322 simplified pattern
 * @param email - Email address to validate
 * @returns true if email is valid format
 */
export function validateEmail(email: string | null | undefined): boolean {
    if (!email || typeof email !== 'string') return false;

    const trimmed = email.trim();
    if (trimmed.length === 0) return false;

    // Simplified RFC 5322 pattern - covers most common email formats
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(trimmed);
}

/**
 * Phone number validation - supports various formats
 * Accepts international and domestic formats
 * @param phone - Phone number to validate
 * @returns true if phone number has valid format and length
 */
export function validatePhone(phone: string | null | undefined): boolean {
    if (!phone || typeof phone !== 'string') return false;

    const trimmed = phone.trim();
    if (trimmed.length === 0) return false;

    // Extract digits only
    const digitsOnly = trimmed.replace(/\D/g, '');

    // Valid phone numbers typically have 10-15 digits
    // 10 for domestic (UK, US), up to 15 for international (E.164 standard)
    const hasValidLength = digitsOnly.length >= 10 && digitsOnly.length <= 15;

    // Must contain at least some digits and common phone characters
    const hasValidCharacters = /^[0-9\s\-\+\(\)\.]+$/.test(trimmed);

    return hasValidLength && hasValidCharacters;
}

/**
 * Determine validation state for UI feedback
 * @param value - Current field value
 * @param validator - Validation function to use
 * @param isDirty - Whether field has been touched/modified
 * @returns Validation state: 'valid' | 'invalid' | 'idle'
 */
export function getValidationState(
    value: string | null | undefined,
    validator: (val: string | null | undefined) => boolean,
    isDirty: boolean = true,
): 'valid' | 'invalid' | 'idle' {
    if (!isDirty || !value || value.trim().length === 0) {
        return 'idle';
    }

    return validator(value) ? 'valid' : 'invalid';
}

/**
 * Combined validation state helper specifically for email fields
 */
export function getEmailValidationState(email: string | null | undefined, isDirty: boolean = true): 'valid' | 'invalid' | 'idle' {
    return getValidationState(email, validateEmail, isDirty);
}

/**
 * Combined validation state helper specifically for phone fields
 */
export function getPhoneValidationState(phone: string | null | undefined, isDirty: boolean = true): 'valid' | 'invalid' | 'idle' {
    return getValidationState(phone, validatePhone, isDirty);
}
